import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #autoOpenVersion = 0;
  #openTimer;
  #cartHeadingLabel;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    window.addEventListener('click', this.#handleAddValidation, true);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    clearTimeout(this.#openTimer);
    this.#autoOpenVersion++;
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    window.removeEventListener('click', this.#handleAddValidation, true);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  // Kaching stops a required-but-incomplete add before the theme receives it.
  // Expose its existing validation message instead of leaving a silent button.
  #handleAddValidation = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[ref="addToCartButton"]');
    const host = trigger?.closest('.product-details')?.querySelector('kaching-bundle');
    if (!host) return;
    requestAnimationFrame(() => {
      const alert = host.querySelector('.kaching-bundles__collection-breaks-alert');
      if (!alert?.getClientRects().length) return;
      alert.setAttribute('role', 'alert');
      alert.tabIndex = -1;
      alert.focus();
    });
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (!this.hasAttribute('auto-open') || event.action !== 'add') {
      event.promise?.then(() => requestAnimationFrame(() => this.#updateStickyState())).catch(() => {});
      return;
    }

    const version = ++this.#autoOpenVersion;
    clearTimeout(this.#openTimer);
    const drawer = this.#themeDrawer;
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    // A successful pack resets its quantities and disables its submit button.
    // Return to the labelled summary instead of an unfocusable disabled button.
    const bundleSummary = event.target instanceof Element && event.target.matches('groovi-bundle-builder')
      ? event.target.querySelector('.groovi-bundle__summary') : null;
    bundleSummary?.setAttribute('tabindex', '-1');
    const returnFocus = sourceModal ? undefined : (
      bundleSummary || event.target?.cartAddTrigger || event.target?.querySelector?.('[ref="addToCartButton"], [data-bundle-submit]')
    );
    let dismissed = false;
    let openedPending = false;
    const isCurrent = () => this.isConnected && version === this.#autoOpenVersion;
    const onDismiss = () => { dismissed = true; };
    drawer?.addEventListener(DrawerCloseEvent.eventName, onDismiss);

    // Quick-add must finish closing before the drawer captures/restores focus.
    // The slow-request deadline may ask it to close before the request finishes.
    const open = (closeSource = false) => {
      if (!isCurrent() || dismissed || drawer?.isOpen) return;
      if (sourceModal?.open) {
        sourceModal.addEventListener('close', () => open(), { once: true });
        if (closeSource) sourceModal.closest('quick-add-dialog')?.closeDialog();
        return;
      }
      drawer?.open({ returnFocus });
      if (this.hasAttribute('data-cart-pending')) this.querySelector('.cart-drawer__pending button')?.focus();
    };
    const failed = () => {
      if (!isCurrent() || !openedPending) return;
      if (dismissed) this.#setPending(false);
      else this.#cancelPendingOpen();
    };

    // Normal responses open already rendered. A slow network still gets an
    // accessible drawer within the 3s budget, without painting stale/empty items.
    this.#openTimer = setTimeout(() => {
      if (!isCurrent() || dismissed || drawer?.isOpen) return;
      openedPending = true;
      this.#setPending(true);
      open(true);
    }, 2400);

    // All synchronous cart-event listeners have now registered their render work.
    queueMicrotask(() => {
      const renderComplete = this.querySelector('cart-items-component')?.cartUpdateComplete;
      event.promise
        ?.then(async ({ detail }) => {
          if (detail?.didError) {
            failed();
            return;
          }
          const rendered = await renderComplete;
          if (!isCurrent()) return;
          clearTimeout(this.#openTimer);
          if (rendered === false) {
            this.#setPending(true, 'O item foi adicionado, mas não foi possível atualizar a visualização. Confira seu carrinho.');
            open(true);
            return;
          }
          this.#setPending(false);
          open();
          requestAnimationFrame(() => this.#updateStickyState());
        })
        .catch((error) => {
          failed();
          if (error?.name !== 'AbortError') console.warn('[cart-drawer] Add failed:', error);
        })
        .finally(() => {
          if (isCurrent()) clearTimeout(this.#openTimer);
          drawer?.removeEventListener(DrawerCloseEvent.eventName, onDismiss);
        });
    });
  };

  #cancelPendingOpen() {
    if (this.#dialog?.open) {
      this.#dialog.addEventListener('close', () => this.#setPending(false), { once: true });
      this.#themeDrawer?.close();
    } else {
      this.#setPending(false);
    }
  }

  #setPending(pending, message = 'Atualizando seu carrinho…') {
    if (pending && !this.hasAttribute('data-cart-pending')) {
      this.#cartHeadingLabel = this.#dialog?.getAttribute('aria-labelledby');
    }
    this.toggleAttribute('data-cart-pending', pending);
    const panel = this.querySelector('.cart-drawer__pending');
    const inner = this.querySelector('.cart-drawer__inner');
    const hadFocus = panel?.contains(document.activeElement);
    if (panel) {
      panel.hidden = !pending;
      panel.querySelector('[role="status"]').textContent = message;
    }
    if (pending) this.#dialog?.setAttribute('aria-labelledby', 'cart-drawer-pending-heading');
    else if (this.#cartHeadingLabel) this.#dialog?.setAttribute('aria-labelledby', this.#cartHeadingLabel);
    if (inner) {
      inner.inert = pending;
      if (pending) inner.setAttribute('aria-busy', 'true');
      else inner.removeAttribute('aria-busy');
    }
    if (!pending && hadFocus) inner?.querySelector('.theme-drawer__close-button')?.focus();
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
